// Postranní menu ve stylu Clauda: nahoře značka a sbalení, pod tím pohledy a další sekce,
// pak „Nedávné“ chaty a dole účet. Nový chat a hledání jsou v horní liště.
// Sbalené menu je úzký pruh s ikonami (stav se pamatuje v prohlížeči).
import { useState, type ReactNode } from 'react';
import {
  AppWindowIcon,
  CaretRightIcon,
  CircleHalfIcon,
  ChatsCircleIcon,
  ClockIcon,
  DotsThreeIcon,
  EyeIcon,
  EyeSlashIcon,
  ChartBarIcon,
  FolderIcon,
  GearSixIcon,
  LockSimpleIcon,
  MicrophoneIcon,
  MoonIcon,
  PencilSimpleIcon,
  PushPinIcon,
  PushPinSlashIcon,
  SidebarSimpleIcon,
  SunIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { DropdownMenu, IconButton, Input, Modal, Button, Tooltip, useConfirm, useToast } from '@smworks-cz/ui-kit';
import { type ConversationDTO, type MeDTO, type Theme, type ViewDTO } from '@sam/shared';
import { api } from '../lib/api';
import { chatPath, navigate, viewPath, type Route } from '../lib/router';
import { keys, queryClient, useConversations, useViews } from '../lib/queries';
import { updateView } from '../lib/views';
import { groupChats } from '../lib/groups';
import { saveTheme } from '../lib/settings';
import { THEME_LABEL, THEME_ORDER } from '../lib/theme';

interface Props {
  me: MeDTO;
  route: Route;
  collapsed: boolean;
  onToggle: () => void;
  onSettings: () => void;
  onNavigate: () => void;
}

const VIEWS_OPEN_KEY = 'sam-side-views-open';
const OLDER_LIMIT = 12;

// Úložiště prohlížeče je jen pohodlí: v soukromém okně může chybět.
function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
function writeFlag(key: string, on: boolean) {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {
    /* nic */
  }
}


export function Sidebar({ me, route, collapsed, onToggle, onSettings, onNavigate }: Props) {
  const { data: convs, isLoading } = useConversations();
  const [renaming, setRenaming] = useState<ConversationDTO | null>(null);
  const [renamingView, setRenamingView] = useState<ViewDTO | null>(null);
  const views = useViews();
  const confirm = useConfirm();
  const { toast } = useToast();
  const [viewsOpen, setViewsOpen] = useState(() => readFlag(VIEWS_OPEN_KEY));
  const [showAll, setShowAll] = useState(false);
  const pinned = (convs ?? []).filter((c) => c.pinnedAt).sort((a, b) => b.pinnedAt!.localeCompare(a.pinnedAt!));
  const groups = groupChats((convs ?? []).filter((c) => !c.pinnedAt));

  const toggleViews = () => {
    setViewsOpen((x) => {
      writeFlag(VIEWS_OPEN_KEY, !x);
      return !x;
    });
  };

  const pin = async (c: ConversationDTO, pinned: boolean) => {
    const pinnedAt = pinned ? new Date().toISOString() : null;
    queryClient.setQueryData<ConversationDTO[]>(keys.conversations, (old) => old?.map((x) => (x.id === c.id ? { ...x, pinnedAt } : x)));
    try {
      await api('PATCH', `/conversations/${c.id}`, { pinned });
    } catch (e) {
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      toast({ variant: 'error', title: (e as Error).message });
    }
  };

  const go = (to: string) => {
    navigate(to);
    onNavigate();
  };

  const remove = async (c: ConversationDTO) => {
    const ok = await confirm({
      title: 'Smazat chat?',
      description: `Chat „${c.title}“ zmizí z historie.`,
      confirmLabel: 'Smazat',
      cancelLabel: 'Zrušit',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api('DELETE', `/conversations/${c.id}`);
      queryClient.setQueryData<ConversationDTO[]>(keys.conversations, (old) => old?.filter((x) => x.id !== c.id));
      if (route.t === 'chat' && route.id === c.id) navigate('/');
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    }
  };

  const removeView = async (v: ViewDTO) => {
    const ok = await confirm({ title: 'Odebrat pohled?', description: `Pohled „${v.title}“ zmizí z menu.`, confirmLabel: 'Odebrat', cancelLabel: 'Zrušit', variant: 'danger' });
    if (!ok) return;
    try {
      await api('DELETE', `/views/${v.id}`);
      queryClient.setQueryData<ViewDTO[]>(keys.views, (old) => old?.filter((x) => x.id !== v.id));
      if (route.t === 'view' && route.id === v.id) navigate('/');
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    }
  };

  if (collapsed) {
    const rail = (label: string, icon: ReactNode, onClick: () => void, active = false) => (
      <Tooltip content={label} position="right">
        <button type="button" className={`rail-btn${active ? ' on' : ''}`} aria-label={label} onClick={onClick}>
          {icon}
        </button>
      </Tooltip>
    );
    return (
      <nav className="side collapsed" aria-label="Menu">
        <Tooltip content="Rozbalit menu (Ctrl+B)" position="right">
          <button type="button" className="rail-brand" onClick={onToggle} aria-label="Rozbalit menu">
            <img src="/favicon.svg" alt="" />
          </button>
        </Tooltip>
        {rail('Moje pohledy', <ChartBarIcon size={18} />, onToggle, route.t === 'view')}
        {rail('Nedávné chaty', <ChatsCircleIcon size={18} />, onToggle, route.t === 'chat')}
        {rail('Přepisy', <MicrophoneIcon size={18} />, () => go('/prepisy'), route.t === 'transcripts')}
        {rail('Projekty (brzy)', <FolderIcon size={18} />, onToggle)}
        <span className="grow" />
        <Tooltip content={`${me.user.name} · Nastavení`} position="right">
          <button type="button" className="rail-me" onClick={onSettings} aria-label="Nastavení účtu">
            <GearSixIcon size={18} />
          </button>
        </Tooltip>
      </nav>
    );
  }

  const chatItem = (c: ConversationDTO) => {
    const on = route.t === 'chat' && route.id === c.id;
    return (
      <div key={c.id} className={`nav-i${on ? ' on' : ''}${c.unread && !on ? ' unread' : ''}`}>
        <a
          href={chatPath(c.id)}
          className="nav-link"
          onClick={(e) => {
            e.preventDefault();
            go(chatPath(c.id));
          }}
          aria-current={on ? 'page' : undefined}
        >
          {c.activeTurnId && <span className="dot live" aria-label="Sam odpovídá" />}
          {c.scheduleId && !c.activeTurnId && <ClockIcon size={14} className="sch-mark" aria-label="Naplánovaná úloha" />}
          <span className="t">{c.title}</span>
          {c.unread && !on && <span className="dot unread" aria-label="Nepřečtený výsledek" />}
        </a>
        <DropdownMenu
          position="bottom-right"
          trigger={
            <button type="button" className="nav-more" aria-label={`Akce pro chat ${c.title}`}>
              <DotsThreeIcon size={18} weight="bold" />
            </button>
          }
          items={[
            {
              label: c.pinnedAt ? 'Odepnout' : 'Připnout',
              icon: c.pinnedAt ? <PushPinSlashIcon size={16} /> : <PushPinIcon size={16} />,
              onClick: () => void pin(c, !c.pinnedAt),
            },
            { label: 'Přejmenovat', icon: <PencilSimpleIcon size={16} />, onClick: () => setRenaming(c) },
            { label: 'Smazat', icon: <TrashIcon size={16} />, danger: true, onClick: () => void remove(c) },
          ]}
        />
      </div>
    );
  };

  return (
    <nav className="side" aria-label="Menu">
      <div className="side-top">
        <a
          href="/"
          className="brand"
          onClick={(e) => {
            e.preventDefault();
            go('/');
          }}
        >
          <img src="/logo.png" alt="" />
          <b>SAM</b>
        </a>
        <span className="side-collapse">
          <IconButton icon={<SidebarSimpleIcon size={18} />} label="Sbalit menu" tooltip="Sbalit menu (Ctrl+B)" variant="ghost" size="sm" onClick={onToggle} />
        </span>
      </div>

      <div className="side-scroll">
        <div className="side-nav">
          <div className={`nav-i${route.t === 'transcripts' ? ' on' : ''}`}>
            <a
              href="/prepisy"
              className="nav-link"
              onClick={(e) => {
                e.preventDefault();
                go('/prepisy');
              }}
              aria-current={route.t === 'transcripts' ? 'page' : undefined}
            >
              <MicrophoneIcon size={16} />
              <span className="t">Přepisy</span>
            </a>
          </div>
          <span className="nav-i disabled" title="Projekty přijdou v další verzi">
            <span className="nav-link">
              <FolderIcon size={16} />
              <span className="t">Projekty</span>
              <span className="soon">brzy</span>
            </span>
          </span>
          <span className="nav-i disabled" title="Rozcestník aplikací přijde v další verzi">
            <span className="nav-link">
              <AppWindowIcon size={16} />
              <span className="t">Aplikace</span>
              <span className="soon">brzy</span>
            </span>
          </span>
        </div>

        {(views.data?.length ?? 0) > 0 && (
          <div className="side-sec">
            <button type="button" className="sec-h sec-toggle" aria-expanded={viewsOpen} onClick={() => toggleViews()}>
              <span>Moje pohledy</span>
              <CaretRightIcon size={11} weight="bold" className="caret" />
            </button>
            {viewsOpen &&
              views.data!.map((v) => {
                const on = route.t === 'view' && route.id === v.id;
                return (
                  <div key={v.id} className={`nav-i${on ? ' on' : ''}${v.locked ? ' locked' : ''}`}>
                    <a
                      href={viewPath(v.id)}
                      className="nav-link"
                      title={v.locked ? 'Bez oprávnění: vaše role na data pohledu nemá právo' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        go(viewPath(v.id));
                      }}
                      aria-current={on ? 'page' : undefined}
                    >
                      {v.locked && <LockSimpleIcon size={13} />}
                      <span className="t">{v.title}</span>
                    </a>
                    <DropdownMenu
                      position="bottom-right"
                      trigger={
                        <button type="button" className="nav-more" aria-label={`Akce pro pohled ${v.title}`}>
                          <DotsThreeIcon size={18} weight="bold" />
                        </button>
                      }
                      items={[
                        { label: 'Přejmenovat', icon: <PencilSimpleIcon size={16} />, onClick: () => setRenamingView(v) },
                        {
                          label: v.onHome ? 'Skrýt z přehledu' : 'Zobrazit na přehledu',
                          icon: v.onHome ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />,
                          onClick: () => void updateView(v.id, { onHome: !v.onHome }).catch((e) => toast({ variant: 'error', title: (e as Error).message })),
                        },
                        { label: 'Odebrat', icon: <TrashIcon size={16} />, danger: true, onClick: () => void removeView(v) },
                      ]}
                    />
                  </div>
                );
              })}
          </div>
        )}

        {pinned.length > 0 && (
          <div className="side-sec">
            <div className="sec-h">Připnuté</div>
            {pinned.map(chatItem)}
          </div>
        )}

        {isLoading && <p className="side-empty">Načítám…</p>}
        {!isLoading && !convs?.length && <p className="side-empty">Zatím žádné chaty.</p>}
        {groups.map((g) => {
          const limited = g.key === 'older' && !showAll ? g.items.slice(0, OLDER_LIMIT) : g.items;
          return (
            <div key={g.key} className="side-sec">
              <div className="sec-h">{g.label}</div>
              {limited.map(chatItem)}
              {g.key === 'older' && g.items.length > OLDER_LIMIT && (
                <button type="button" className="side-more" onClick={() => setShowAll((x) => !x)}>
                  {showAll ? 'Zobrazit méně' : `Zobrazit vše (${g.items.length})`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="me-row">
        <button type="button" className="me" onClick={onSettings} aria-label={`${me.user.name}: nastavení`}>
          <b>{me.user.name}</b>
          <GearSixIcon size={15} className="me-gear" />
        </button>
        <ThemeToggle theme={me.theme} />
      </div>

      <RenameModal
        item={renaming}
        title="Přejmenovat chat"
        onClose={() => setRenaming(null)}
        save={async (id, title) => {
          await api('PATCH', `/conversations/${id}`, { title });
          queryClient.setQueryData<ConversationDTO[]>(keys.conversations, (old) => old?.map((x) => (x.id === id ? { ...x, title } : x)));
          void queryClient.invalidateQueries({ queryKey: keys.conversation(id) });
        }}
      />
      <RenameModal
        item={renamingView}
        title="Přejmenovat pohled"
        onClose={() => setRenamingView(null)}
        save={async (id, title) => {
          await api('PATCH', `/views/${id}`, { title });
          queryClient.setQueryData<ViewDTO[]>(keys.views, (old) => old?.map((x) => (x.id === id ? { ...x, title } : x)));
          void queryClient.invalidateQueries({ queryKey: keys.view(id) });
        }}
      />
    </nav>
  );
}

/** Přepínač vzhledu vedle jména: automaticky → světlý → tmavý. */
function ThemeToggle({ theme }: { theme: Theme }) {
  const { toast } = useToast();
  const next = THEME_ORDER[(THEME_ORDER.indexOf(theme) + 1) % THEME_ORDER.length];
  const icon = theme === 'light' ? <SunIcon size={17} /> : theme === 'dark' ? <MoonIcon size={17} /> : <CircleHalfIcon size={17} />;
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label={`Vzhled: ${THEME_LABEL[theme]}. Přepnout na ${THEME_LABEL[next].toLowerCase()}.`}
      title={`Vzhled: ${THEME_LABEL[theme]}`}
      onClick={() => void saveTheme(next).catch((e) => toast({ variant: 'error', title: (e as Error).message }))}
    >
      {icon}
    </button>
  );
}

function RenameModal({
  item: conv,
  title: heading,
  onClose,
  save: doSave,
}: {
  item: { id: string; title: string } | null;
  title: string;
  onClose: () => void;
  save: (id: string, title: string) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const [lastId, setLastId] = useState<string | null>(null);
  if (conv && conv.id !== lastId) {
    setLastId(conv.id);
    setTitle(conv.title);
  }
  const save = async () => {
    if (!conv || !title.trim()) return;
    setSaving(true);
    try {
      await doSave(conv.id, title.trim());
      onClose();
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={!!conv}
      onClose={onClose}
      title={heading}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={!title.trim()}>
            Uložit
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus fullWidth aria-label="Název" />
      </form>
    </Modal>
  );
}
